import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Confirm, EmptyState, useMeta } from "../components/Shared";
import { fmtDate, statusBadgeClass } from "../lib/format";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { CheckCircle2, XCircle, Trash2, UserCog } from "lucide-react";
import { toast } from "sonner";
import { TableSkeleton } from "../components/Skeletons";

const roleBadgeClass = (role) => {
  const r = (role || "").toLowerCase();
  if (r.includes("ceo") && r.includes("assistant")) return "bg-indigo-50 text-indigo-700 border-indigo-100";
  if (r.includes("ceo")) return "bg-purple-50 text-purple-700 border-purple-100";
  if (r.includes("general")) return "bg-blue-50 text-blue-700 border-blue-100";
  if (r.includes("financial")) return "bg-emerald-50 text-emerald-700 border-emerald-100";
  if (r.includes("manager")) return "bg-amber-50 text-amber-700 border-amber-100";
  return "bg-slate-100 text-slate-600 border-slate-200";
};

const RoleBadge = ({ role, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${roleBadgeClass(role)}`}>{role || "—"}</span>
);

export default function Users() {
  const { user } = useAuth();
  const meta = useMeta();
  const [approve, setApprove] = useState(null);
  const [roleSel, setRoleSel] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);

  const qc = useQueryClient();
  const { data: rows = [], isLoading: rowsLoading } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users").then((r) => r.data) });
  const load = () => qc.invalidateQueries();

  const doApprove = async () => {
    try { await api.patch(`/users/${approve.id}/approve`, { role: roleSel }); toast.success("User approved"); setApprove(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reject = async (u) => { try { await api.patch(`/users/${u.id}/reject`); toast.success("User rejected"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const setStatus = async (u, status) => { try { await api.patch(`/users/${u.id}/status`, { status }); toast.success("Status updated"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const setRole = async (u, role) => { try { await api.patch(`/users/${u.id}/role`, { role }); toast.success("Role updated"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const remove = async (u) => { try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const toggleSel = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const bulk = async (action) => {
    try { const { data } = await api.post("/users/bulk", { ids: sel, action }); toast.success(`${action} applied to ${data.affected} user(s)`); setSel([]); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk action failed"); }
  };

  const pending = rows.filter((r) => r.status === "pending");
  const ql = q.trim().toLowerCase();
  const others = rows.filter((r) => r.status !== "pending")
    .filter((r) => !ql || `${r.name} ${r.email} ${r.role} ${r.status}`.toLowerCase().includes(ql));

  return (
    <div>
      <PageHeader title="User Approvals" subtitle="Approve registrations, assign roles and manage accounts" />

      {pending.length > 0 && (
        <div className="bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden mb-6" data-testid="awaiting-role-section">
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 text-sm font-semibold text-amber-800">
            Awaiting Role Assignment ({pending.length})
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Activated</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {pending.map((u) => (
                <TableRow key={u.id} data-testid={`pending-user-${u.id}`}>
                  <TableCell className="font-medium text-slate-800">{u.name}</TableCell>
                  <TableCell className="text-slate-600">{u.email}</TableCell>
                  <TableCell className="text-slate-500">{fmtDate(u.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="stems-actions">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-user-${u.id}`} onClick={() => { setApprove(u); setRoleSel(meta.roles[1] || "General Manager"); }}>
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Assign Role
                      </Button>
                      <Confirm onConfirm={() => reject(u)} title="Reject this account?" description={`${u.name} will not be able to sign in.`} actionLabel="Reject"
                        trigger={<Button size="sm" variant="outline" className="text-red-600" data-testid={`reject-user-${u.id}`}><XCircle className="h-4 w-4 mr-1" /> Reject</Button>} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h3 className="font-heading font-semibold text-slate-800">All Users</h3>
            {sel.length > 0 && (
              <div className="flex items-center gap-2" data-testid="bulk-action-bar">
                <span className="text-xs text-slate-500">{sel.length} selected</span>
                <Button size="sm" variant="outline" data-testid="bulk-suspend" onClick={() => bulk("suspend")}>Suspend</Button>
                <Button size="sm" variant="outline" data-testid="bulk-activate" onClick={() => bulk("activate")}>Activate</Button>
                <Confirm onConfirm={() => bulk("delete")} title={`Delete ${sel.length} user(s)?`} description="This permanently removes the selected accounts."
                  trigger={<Button size="sm" variant="outline" className="text-red-600" data-testid="bulk-delete">Delete</Button>} />
              </div>
            )}
          </div>
          <input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, role…"
            className="h-9 w-64 max-w-[45%] rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100" />
        </div>
        {rowsLoading ? <TableSkeleton cols={6} /> : others.length === 0 ? <EmptyState text={ql ? "No users match your search." : "No active users yet."} /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-10">
                <input type="checkbox" data-testid="bulk-select-all"
                  checked={others.length > 0 && others.every((u) => u.id === user.id || sel.includes(u.id))}
                  onChange={(e) => setSel(e.target.checked ? others.filter((u) => u.id !== user.id).map((u) => u.id) : [])}
                  className="h-4 w-4 rounded border-slate-300" />
              </TableHead>
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {others.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell>
                    {u.id !== user.id && (
                      <input type="checkbox" data-testid={`bulk-select-${u.id}`}
                        checked={sel.includes(u.id)} onChange={() => toggleSel(u.id)}
                        className="h-4 w-4 rounded border-slate-300" />
                    )}
                  </TableCell>
                  <TableCell className="font-medium text-slate-800">{u.name}{u.id === user.id && <span className="ml-2 text-xs text-slate-400">(you)</span>}</TableCell>
                  <TableCell className="text-slate-600">{u.email}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <RoleBadge role={u.role} testId={`role-badge-${u.id}`} />
                      {u.id !== user.id && (
                        <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
                          <SelectTrigger className="h-8 w-44" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{meta.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </div>
                  </TableCell>
                  <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${statusBadgeClass(u.status)}`}>{u.status}</span></TableCell>
                  <TableCell className="text-right">
                    {u.id !== user.id && (
                      <div className="stems-actions">
                        {u.status === "active"
                          ? <Button size="sm" variant="outline" data-testid={`suspend-user-${u.id}`} onClick={() => setStatus(u, "suspended")}>Suspend</Button>
                          : <Button size="sm" variant="outline" data-testid={`activate-user-${u.id}`} onClick={() => setStatus(u, "active")}>Activate</Button>}
                        <Confirm onConfirm={() => remove(u)} title="Delete user?" description={`This permanently removes ${u.name}.`}
                          trigger={<Button size="icon" variant="ghost" className="text-red-600" data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!approve} onOpenChange={(v) => !v && setApprove(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserCog className="h-5 w-5" /> Assign role to {approve?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-sm">Assign a role</Label>
            <Select value={roleSel} onValueChange={setRoleSel}>
              <SelectTrigger data-testid="approve-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{meta.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprove(null)}>Cancel</Button>
            <Button onClick={doApprove} data-testid="confirm-approve-button" className="bg-emerald-600 hover:bg-emerald-700">Assign Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
